import { useSeo } from "@/lib/seo";
import { ROUTES } from "@/lib/routes";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Stamp } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/i18n";
import Logo from "@/components/Logo";
import Background from "@/components/landing/Background";
import { RichText } from "@/components/RichText";
import { QuoteForm } from "@/components/landing/QuoteForm";

const ACCENTS = ["bg-brand-teal", "bg-brand-red", "bg-brand-yellow", "bg-brand-navy dark:bg-white"];

export function pickLang(page, lang) {
  if (!page) return null;
  return lang === "pl" ? page.pl : page.en || page.pl;
}

function Inline({ text }) {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
  return <>{parts.map((p, i) => (p.startsWith("**") ? <strong key={i} className="font-semibold text-slate-900 dark:text-white">{p.slice(2, -2)}</strong> : <RichText key={i} text={p} />))}</>;
}

export function Body({ text, testid }) {
  const blocks = String(text || "").split(/\n\s*\n/).map((b) => b.trim()).filter(Boolean);
  return (
    <div data-testid={testid} className="space-y-3 text-sm sm:text-base text-slate-600 dark:text-slate-300 leading-relaxed">
      {blocks.map((b, i) => {
        const raw = b.split("\n").filter((l) => l.trim());
        const lines = raw.map((l) => l.trim());
        const isSub = (l) => /^\s{2,}(\S.*)$/.test(l) && !/^\s*\d+[.)]\s/.test(l);
        if (raw.some(isSub) && /^\d+[.)]\s/.test(lines[0])) {
          const items = [];
          raw.forEach((l) => {
            if (isSub(l) && items.length) items[items.length - 1].subs.push(l.trim());
            else items.push({ text: l.trim().replace(/^\d+[.)]\s/, ""), subs: [] });
          });
          return (
            <ol key={i} className="space-y-2.5">
              {items.map((it, j) => (
                <li key={j} className="flex gap-3"><span className="font-mono text-xs font-bold text-brand-teal mt-1 shrink-0 w-5">{j + 1}.</span>
                  <div className="flex-1"><Inline text={it.text} />
                    {it.subs.length > 0 && <ul className="mt-1.5 space-y-1 pl-1">{it.subs.map((sub, k) => <li key={k} className="flex gap-2 text-[0.95em]"><span className="text-brand-teal shrink-0">{/^[a-z][.)]\s/.test(sub) ? sub.slice(0, 2) : "–"}</span><span><Inline text={sub.replace(/^[a-z][.)]\s/, "")} /></span></li>)}</ul>}
                  </div>
                </li>
              ))}
            </ol>
          );
        }
        if (lines.every((l) => /^[-•*]\s/.test(l))) {
          return (
            <ul key={i} className="space-y-2">
              {lines.map((l, j) => (
                <li key={j} className="flex gap-3"><span className="w-1.5 h-1.5 rounded-full bg-brand-teal mt-2.5 shrink-0" /><span><Inline text={l.replace(/^[-•*]\s/, "")} /></span></li>
              ))}
            </ul>
          );
        }
        if (lines.every((l) => /^\d+[.)]\s/.test(l))) {
          return (
            <ol key={i} className="space-y-2">
              {lines.map((l, j) => (
                <li key={j} className="flex gap-3"><span className="font-mono text-xs font-bold text-brand-teal mt-1 shrink-0 w-5">{j + 1}.</span><span><Inline text={l.replace(/^\d+[.)]\s/, "")} /></span></li>
              ))}
            </ol>
          );
        }
        if (lines.length > 1 && lines.every((l) => l.startsWith("|"))) {
          const rows = lines.map((l) => l.replace(/^\||\|$/g, "").split("|").map((c) => c.trim()));
          const [head, ...body] = rows;
          return (
            <div key={i} className="overflow-x-auto -mx-1">
              <table className="w-full text-sm border-collapse min-w-[540px]">
                <thead><tr>{head.map((c, j) => <th key={j} className="text-left font-semibold text-slate-900 dark:text-white bg-slate-100 dark:bg-slate-800 px-3 py-2.5 first:rounded-tl-lg last:rounded-tr-lg">{c}</th>)}</tr></thead>
                <tbody>{body.map((r, k) => (
                  <tr key={k} className="border-b border-slate-200 dark:border-slate-800 last:border-0 align-top">
                    {r.map((c, j) => <td key={j} className={`px-3 py-2.5 ${j === 0 ? "font-semibold text-slate-900 dark:text-white" : ""}`}><Inline text={c} /></td>)}
                  </tr>
                ))}</tbody>
              </table>
            </div>
          );
        }
        return <p key={i}><Inline text={lines.join(" ")} /></p>;
      })}
    </div>
  );
}

// Sections with a table go full width; a leftover single card in a row is widened too.
function wideFlags(sections, all = false) {
  if (all) return sections.map((s) => [s, true]);
  const out = [];
  let col = 0;
  sections.forEach((s) => {
    const wide = /^\|/m.test(s.body);
    if (wide) { if (col === 1) out[out.length - 1][1] = true; col = 0; } else col = (col + 1) % 2;
    out.push([s, wide]);
  });
  if (col === 1) out[out.length - 1][1] = true;
  return out;
}

export default function ContentPage({ slug = "legalization", singleColumn = false }) {
  const { t, lang } = useT();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const p = pickLang(page, lang);

  useEffect(() => { setPage(null); api.get(`/pages/${slug}`).then((r) => setPage(r.data)).catch(() => {}); }, [slug]);
  useSeo({ title: p?.title || "", description: p?.subtitle || p?.intro?.slice?.(0, 160) || undefined, path: slug === "owu" ? ROUTES.owu : ROUTES.legalization, breadcrumbs: [{ name: p?.title || slug, path: slug === "owu" ? ROUTES.owu : ROUTES.legalization }] });

  return (
    <div data-testid={`${slug}-page`} className="min-h-screen text-foreground">
      <Background />
      <header className="sticky top-0 z-40 border-b border-border bg-card/80 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" aria-label="Wirtualne biuro Warszawa — Twoje Biuro, strona główna"><Logo className="h-8 text-slate-900 dark:text-white" showTagline={false} /></Link>
          <Link to="/" data-testid={`${slug}-back-link`} className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border rounded-full px-4 py-2 hover:bg-muted transition-colors">
            <ArrowLeft size={13} /> {t.footer.back_home}
          </Link>
        </div>
      </header>
      {p && (
        <main className="max-w-5xl mx-auto px-4 sm:px-8 py-12 sm:py-20">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground font-semibold">
            <span aria-hidden="true" className="inline-block w-6 h-[3px] rounded-full bg-brand-red mr-3 align-middle" />{p.subtitle || t.nav[slug]}
          </p>
          <div className="mt-4 flex items-start gap-5">
            <div className="hidden sm:flex w-14 h-14 rounded-2xl bg-brand-red/10 dark:bg-brand-red/25 text-brand-red dark:text-red-300 items-center justify-center shrink-0 mt-1"><Stamp size={26} /></div>
            <h1 data-testid={`${slug}-title`} className="font-display font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl">{p.title}</h1>
          </div>
          {p.intro && <div className="mt-6 max-w-3xl"><Body text={p.intro} testid="legalization-intro" /></div>}
          {slug === "legalization" ? (
            <div id="wycena" className="mt-10 max-w-3xl"><QuoteForm /></div>
          ) : p.cta_label && (
            <div className="mt-8">
              <button type="button" data-testid={`${slug}-cta-top`} onClick={() => navigate("/#biuro-wirtualne-kontakt")} className="group inline-flex items-center gap-2 bg-brand-red hover:bg-red-700 text-white text-sm font-semibold px-6 py-3 rounded-full transition-colors">
                {p.cta_label}
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </div>
          )}
          {p.sections.length > 0 && (
            <div data-testid={`${slug}-sections`} className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
              {wideFlags(p.sections, singleColumn).map(([s, wide], i) => (
                <div key={i} data-testid={`${slug}-section-${i + 1}`} className={`bg-white/85 dark:bg-slate-900/85 backdrop-blur border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-8 ${wide ? "md:col-span-2" : ""}`}>
                  <span aria-hidden="true" className={`block w-8 h-[3px] rounded-full ${ACCENTS[i % ACCENTS.length]}`} />
                  {s.heading && <h2 className="font-display font-bold text-lg md:text-xl tracking-tight mt-4">{s.heading}</h2>}
                  <div className="mt-3"><Body text={s.body} /></div>
                </div>
              ))}
            </div>
          )}
          {(p.cta2_label || p.cta_label) && <div className="mt-12">
            <button type="button" data-testid={`${slug}-cta`} onClick={() => (slug === "legalization" ? document.getElementById("wycena")?.scrollIntoView({ behavior: "smooth" }) : navigate("/#biuro-wirtualne-kontakt"))} className="group inline-flex items-center gap-2 bg-brand-teal hover:bg-brand-tealDark text-white text-sm font-semibold px-6 py-3 rounded-full transition-colors">
              {p.cta2_label || p.cta_label}
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </div>}
        </main>
      )}
    </div>
  );
}
